import React from 'react';
import { BodyText, CheckboxInput, DataGrid, NavigationItem } from 'ui-toolkit';
import styled from 'styled-components';
import { eDocumentVisibilityType } from '../../../types/order/documents/eDocumentVisibilityType';
import { getUserName } from '../../../utilities/users/getUserName';
import { IUserData } from '../../../types/users/IUser';
import { IStandardDocumentVersionData } from '../../../types/order/documents/shared/interfaces';
import { downloadDocument } from 'API/order/documents/downloadDocument';
import { DateFormat, getDateString } from '../../../ui-toolkit/utilities/getDateString';

export interface IDocumentHistoryTableProps {
  isAmericanDigital: boolean;
  documents: IStandardDocumentVersionData[];
  dataLoading: boolean;
  users: IUserData[];
  onPublish: (documentId: string, versionId: string, visibilityType: eDocumentVisibilityType) => void;
  onUnpublish: (documentId: string, versionId: string, visibilityType: eDocumentVisibilityType) => void;
  isZillowClosingEnabled: boolean;
}

export const DocumentHistoryTable: React.FC<IDocumentHistoryTableProps> = ({
  isAmericanDigital,
  documents,
  dataLoading,
  users,
  onPublish,
  onUnpublish,
  isZillowClosingEnabled,
}) => {
  const standardDocuments = documents.slice().sort((a, b) => (a.fileName > b.fileName ? 1 : -1));

  return (
    <DocumensTable
      columns={[
        {
          text: 'File',
          dataField: 'fileName',
          sort: true,
          formatter: ((cell: string, row: IStandardDocumentVersionData) => (
            <NavigationItem
              text={cell}
              onClick={() => downloadDocument(row.id, row.versionId, row.fileName)}
              isInline
            />
          )) as (cell: any, row?: object) => React.ReactNode,
        },
        {
          dataField: 'updatedAt',
          formatter: (cell) => <BreakWordText>{getDateString(DateFormat.hugeDateTime, cell, true)}</BreakWordText>,
          text: 'Updated At',
          sort: true,
        },
        {
          dataField: 'uploaderType',
          formatter: ((cell: string, row: IStandardDocumentVersionData) => (
            <>{cell ? <BodyText>{getUserName(users, isZillowClosingEnabled, row.uploaderType, row.uploaderId)}</BodyText> : null}</>
          )) as (cell: any, row?: object) => React.ReactNode, //temporary cast until react-bootstrap-table-next types are fixed
          text: 'Updated By',
          sort: true,
        },
        {
          dataField: 'clientPublishedAt',
          formatter: (cell) => (
            <>{cell ? <BreakWordText>{getDateString(DateFormat.hugeDateTime, cell, true)}</BreakWordText> : null}</>
          ),
          text: <WrapBoldText>Published At</WrapBoldText>,
          sort: true,
        },
        {
          dataField: 'clientPublisherId',
          formatter: ((cell: string, row: IStandardDocumentVersionData) => (
            <>
              {cell ? (
                <BodyText>{getUserName(users, isZillowClosingEnabled, row.clientPublisherType, row.clientPublisherId)}</BodyText>
              ) : null}
            </>
          )) as (cell: any, row?: object) => React.ReactNode, //temporary cast until react-bootstrap-table-next types are fixed
          text: <WrapBoldText>Published By</WrapBoldText>,
          sort: true,
        },
        {
          dataField: 'vendorPublishedAt',
          formatter: ((cell: string, row: IStandardDocumentVersionData) => (
            <CheckboxInput
              value={!!cell}
              onChange={(val) =>
                val
                  ? onPublish(row.id, row.versionId, eDocumentVisibilityType.Vendor)
                  : onUnpublish(row.id, row.versionId, eDocumentVisibilityType.Vendor)
              }
            />
          )) as (cell: any, row?: object) => React.ReactNode, //temporary cast until react-bootstrap-table-next types are fixed
          text: <WrapBoldText>Published to Vendor</WrapBoldText>,
          sort: true,
        },
        {
          dataField: 'id',
          text: 'Action',
          formatter: ((cell: string, document: IStandardDocumentVersionData) => (
            <div>
              {isAmericanDigital && document.category.includes('policy') ? null : (
                <>
                  {document.clientPublishedAt ? (
                    <NavigationItem
                      text={'Unpublish'}
                      onClick={() => onUnpublish(document.id, document.versionId, eDocumentVisibilityType.Client)}
                      isActive
                    />
                  ) : (
                    <NavigationItem
                      text={'Publish'}
                      onClick={() => onPublish(document.id, document.versionId, eDocumentVisibilityType.Client)}
                      isActive
                    />
                  )}
                </>
              )}
            </div>
          )) as (cell: any, row?: object) => React.ReactNode, //temporary cast until react-bootstrap-table-next types are fixed
        },
      ]}
      keyField={'versionId'}
      loading={dataLoading}
      data={standardDocuments}
    />
  );
};

const DocumensTable = styled(DataGrid)`
  td {
    border: 1px solid ${(props) => props.theme.colors.lightGray} !important;
    overflow: hidden;
    word-break: break-all;
    * {
      white-space: pre-line;
      line-height: 1.3;
      padding: 0;
    }
  }

  thead tr th {
    border: 1px solid ${(props) => props.theme.colors.lightGray} !important;
    border-bottom: 3px solid ${(props) => props.theme.colors.lightGray} !important;
  }
`;

const WrapBoldText = styled.span`
  white-space: pre-line;
`;

const BreakWordText = styled(BodyText)`
  word-break: break-word;
`;
